import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  Inject,
  makeStateKey,
  OnDestroy,
  OnInit,
  Optional,
  PLATFORM_ID,
  StateKey,
  TransferState,
  ViewEncapsulation,
} from '@angular/core';
import {LinkService} from '../../services/link.service';
import {filter} from 'rxjs/operators';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {SeoMetaService} from '../../services/seo-meta.service';
import {MetaTag} from '../../types/MetaTag';
import {MetaTypesEnum} from '../../enums/SeoEnums';
import {SidebarNavService} from '../sidebar-nav/sidebar-nav.service';
import {LayoutService} from 'src/app/modules/layout/layout.service';
import {DOCUMENT, isPlatformBrowser, isPlatformServer} from '@angular/common';
import {WindowSizeService} from '../../services/window-size.service';
import {UrlBuilderService} from '../../services/url-builder.service';
import {DeviceTypeEnum} from '../../enums/DeviceTypeEnum';
import {TransferStateKeyEnum} from '../../enums/TransferStateKeyEnum';
import {LoaderService} from '../../services/loader.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private routeListener: Subscription;

  private layoutService = inject(LayoutService);
  public areLayoutElementsHidden = this.layoutService.areLayoutElementsHidden;
  public shouldUseNarrowMainContainer = this.layoutService.shouldUseNarrowMainContainer;
  public shouldUseGreyBackground = this.layoutService.shouldUseGreyBackground;
  private sidebarNavService = inject(SidebarNavService);
  public isMenuOpen = this.sidebarNavService.isMenuOpen;

  constructor(
    private readonly linkService: LinkService,
    private readonly router: Router,
    private metaService: SeoMetaService,
    private windowSizeService: WindowSizeService,
    private urlBuilder: UrlBuilderService,
    private transferState: TransferState,
    private loaderService: LoaderService,
    @Inject(DOCUMENT) private doc: any,
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional()
    @Inject('GET_DEVICETYPE')
    private getDeviceType: () => DeviceTypeEnum,
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateWindowSizes();
  }

  public ngOnInit() {
    this.linkService.startRouteListener();
    this.startRouteListener();
    this.updateWindowSizes();
  }

  public ngOnDestroy() {
    this.routeListener.unsubscribe();
  }

  public closeSidebarNav(): void {
    this.sidebarNavService.closeSidebarNav();
  }

  public startRouteListener(): void {
    this.routeListener = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationStart))
      .subscribe((event: NavigationEnd | NavigationStart) => this.handleRouteChange(event));
  }

  private updateWindowSizes(): void {
    const DEVICE_TYPE_STATE_KEY: StateKey<DeviceTypeEnum> = makeStateKey(TransferStateKeyEnum.DEVICE_TYPE);

    const WINDOW_SIZE_STATE_KEY: StateKey<Array<number>> = makeStateKey(TransferStateKeyEnum.WINDOW_SIZE);

    if (isPlatformBrowser(this.platformId)) {
      this.windowSizeService.UpdateWindowSize([window.innerWidth, window.innerHeight]);

      const deviceType: DeviceTypeEnum = this.transferState.get<any>(DEVICE_TYPE_STATE_KEY, '');
      const windowSize: Array<number> = this.transferState.get<any>(WINDOW_SIZE_STATE_KEY, []);
    }
    if (isPlatformServer(this.platformId)) {
      const deviceType: DeviceTypeEnum = this.getDeviceType();

      if (deviceType === DeviceTypeEnum.MOBILE) {
        this.windowSizeService.UpdateWindowSize([375, 667]);
        this.transferState.set(WINDOW_SIZE_STATE_KEY, [375, 667]);
      } else if (deviceType === DeviceTypeEnum.TABLET) {
        this.windowSizeService.UpdateWindowSize([768, 1024]);
        this.transferState.set(WINDOW_SIZE_STATE_KEY, [768, 1024]);
      } else {
        this.windowSizeService.UpdateWindowSize([1440, 1200]);
        this.transferState.set(WINDOW_SIZE_STATE_KEY, [1440, 1200]);
      }

      this.transferState.set(DEVICE_TYPE_STATE_KEY, deviceType);
    }
  }

  private handleRouteChange(event: NavigationEnd | NavigationStart): void {
    if (event instanceof NavigationStart) {
      this.loaderService.addToQueue({
        id: this.loaderService.generateQueueId(),
        title: '',
        description: '',
      });
    }
    if (event instanceof NavigationEnd) {
      this.loaderService.clearQueue();
      this.conditionalRedirectIfTrailingCharacterExists(event);
      let setDefaultNoIndex: boolean = false;
      //default noindex, nofollow set
      if (setDefaultNoIndex) {
        const tag: MetaTag = {
          value: 'noindex,nofollow',
          type: MetaTypesEnum.NAME,
          field: 'robots',
        };
        this.metaService.addMetaTag(tag);
      }
    }
  }

  private conditionalRedirectIfTrailingCharacterExists(event: NavigationEnd): void {
    if (event.url !== this.urlBuilder.removeTrailingCharacters(event.url)) {
      this.router.navigateByUrl(this.urlBuilder.removeTrailingCharacters(event.url));
    }
  }
}
